import React from 'react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { AlertTriangle, Loader2 } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { FounderTransactionKind } from '../../../api/treasury';
import { TransactionDialogProps } from '../types';
import { KIND_LABELS } from './TransactionsTable';

const KIND_HINTS: Record<FounderTransactionKind, string> = {
  contribution: 'Основатель положил деньги на счёт компании: касса и долг перед ним растут.',
  reimbursement: 'Компания вернула вложенное: касса и долг падают.',
  profit: 'Основатель забрал заработанное: падает только касса.',
};

export function TransactionDialog({
  isDark,
  isOpen,
  onOpenChange,
  founders,
  formData,
  onFormDataChange,
  onSubmit,
  isEditing,
  isSubmitting,
  debtByFounder,
  profitAvailableByFounder,
  formatMoney,
}: TransactionDialogProps) {
  const inputCls = cn(
    isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200',
  );
  const labelCls = cn(isDark ? 'text-slate-300' : 'text-slate-700');

  // Перебор не запрещаем — реальность бывает грязной, — но показываем явно.
  const limit =
    formData.kind === 'reimbursement'
      ? debtByFounder[formData.payerId]
      : formData.kind === 'profit'
        ? profitAvailableByFounder[formData.payerId]
        : undefined;
  const isOverLimit = limit !== undefined && formData.amount > limit;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className={cn('max-w-xl', isDark ? 'bg-[#1A1A22] border-slate-700' : 'bg-white')}>
        <DialogHeader>
          <DialogTitle className={cn(isDark ? 'text-white' : 'text-[#111111]')}>
            {isEditing ? 'Изменить движение' : 'Добавить движение'}
          </DialogTitle>
          <DialogDescription className={cn(isDark ? 'text-slate-400' : 'text-slate-500')}>
            {KIND_HINTS[formData.kind]}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className={labelCls}>Дата *</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => onFormDataChange((prev) => ({ ...prev, date: e.target.value }))}
                className={inputCls}
              />
            </div>
            <div className="space-y-2">
              <Label className={labelCls}>Основатель *</Label>
              <Select
                value={formData.payerId}
                onValueChange={(value) => onFormDataChange((prev) => ({ ...prev, payerId: value }))}
              >
                <SelectTrigger className={inputCls}>
                  <SelectValue placeholder="Выберите основателя" />
                </SelectTrigger>
                <SelectContent className={cn(isDark ? 'bg-[#1A1A22] border-slate-700' : 'bg-white')}>
                  {founders.map((founder) => (
                    <SelectItem key={founder.id} value={founder.id}>
                      {founder.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label className={labelCls}>Вид движения *</Label>
            <Select
              value={formData.kind}
              onValueChange={(value) =>
                onFormDataChange((prev) => ({ ...prev, kind: value as FounderTransactionKind }))
              }
            >
              <SelectTrigger className={inputCls}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className={cn(isDark ? 'bg-[#1A1A22] border-slate-700' : 'bg-white')}>
                {(Object.keys(KIND_LABELS) as FounderTransactionKind[]).map((kind) => (
                  <SelectItem key={kind} value={kind}>
                    {KIND_LABELS[kind]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className={labelCls}>Сумма, UZS *</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={formData.amount || ''}
              onChange={(e) =>
                onFormDataChange((prev) => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))
              }
              className={inputCls}
            />
            {limit !== undefined && (
              <p
                className={cn(
                  'text-xs flex items-center gap-1',
                  isOverLimit ? 'text-amber-500' : isDark ? 'text-slate-500' : 'text-slate-400',
                )}
              >
                {isOverLimit && <AlertTriangle className="w-3 h-3" />}
                {formData.kind === 'reimbursement' ? 'Долг перед ним' : 'Доступно взять'}:{' '}
                {formatMoney(limit)}
                {isOverLimit && ' — берёте больше, чем есть'}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label className={labelCls}>Комментарий</Label>
            <Input
              type="text"
              placeholder="Например: перевод на карту 18 августа"
              value={formData.comment || ''}
              onChange={(e) => onFormDataChange((prev) => ({ ...prev, comment: e.target.value }))}
              className={inputCls}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className={cn(isDark ? 'border-slate-700 text-slate-300 hover:bg-slate-800' : 'border-slate-200')}
          >
            Отмена
          </Button>
          <Button
            onClick={onSubmit}
            disabled={isSubmitting}
            className="bg-[#C9A227] hover:bg-[#C9A227]/90"
          >
            {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {isEditing ? 'Сохранить' : 'Добавить'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
